import {
  FC,
  useCallback,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import styles from "./CatalogChipsRow.module.scss";
import { Chips } from "../../../../components/Chips";
import { useCatalogFilter } from "../../../../../hooks/useCatalogFilter";
import { useTranslation } from "next-i18next";
import { useAppSelector } from "../../../../../store/app";
import { getWordEnding } from "../../../../../utils/getWordEnding";
import {
  IFirstRowItem,
} from "../../../../../types/catalogPage";
import { useDebounce } from "../../../../../hooks/useDebounce";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowIcon } from "../../../../icons/ArrowIcon";
import { CircleButton } from "../../../../components/Buttons";
import { firstRowIcons, predefinedPrices } from "../CatalogFilter/constants";
import cl from "classnames";
import { P2, P45 } from "../../../../components/Typography";
import { getFilteredProperties } from "../../../../../hooks/useFilteredProperties";
import { SvgGradient } from "../../../../icons/catalog-filter";
import { useWindowResizeObserver } from "../../../../../hooks/useWindowResizeObserver";
import { SvgCross } from "../../../../icons/SvgCross";

const beachDistance = {
  1: "walkProximity",
  2: "walkDistance",
  3: "shortRide",
};

interface ICatalogChipsRow {
  firstRowItems: IFirstRowItem[];
}
export const CatalogChipsRow: FC<ICatalogChipsRow> = ({ firstRowItems }) => {
  const {
    query: q,
    removeSingleParam,
    deleteParam,
    deleteParams,
    setParam,
    removeMultipleParams,
    isSelection,
  } = useCatalogFilter();
  const query = useDeferredValue(q);
  const {
    t,
    i18n: { language },
  } = useTranslation("index");
  const [isPending, startTransition] = useTransition();

  const { isMobile } = useWindowResizeObserver();

  const properties = useAppSelector((state) => state.catalog.properties);
  const isFixed = useAppSelector((state) => state.catalog.isFixedFilter);

  const { value: currency, symbol } = useAppSelector((state) => state.settings.currency);

  const [selection, setSelection] = useState<IFirstRowItem | undefined>(
    firstRowItems.find((i) => i.query.quickSelection === query.quickSelection),
  );

  const [isScrollEnd, setIsScrollEnd] = useState(false);
  const [isScrollStart, setIsScrollStart] = useState(true);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    dragFree: true,
    duration: 15,
    slidesToScroll: 2,
    align: "start",
  });

  const debounceSetScrollEnd = useDebounce((val: boolean) => {
    setIsScrollEnd(val);
  }, 250);
  const debounceSetScrollStart = useDebounce((val: boolean) => {
    setIsScrollStart(val);
  }, 250);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    emblaApi?.on("select", (e) => {
      debounceSetScrollEnd(!e.canScrollNext());
    });
    () => {
      emblaApi?.off("select", (e) => {
        debounceSetScrollEnd(!e.canScrollNext());
      });
    };
  }, [debounceSetScrollEnd, emblaApi]);

  useEffect(() => {
    emblaApi?.on("select", (e) => {
      debounceSetScrollStart(!e.canScrollPrev());
    });
    () => {
      emblaApi?.off("select", (e) => {
        debounceSetScrollStart(!e.canScrollPrev());
      });
    };
  }, [debounceSetScrollStart, emblaApi]);

  const [filterMin, filterMax] = useAppSelector((state) => state.catalog.filteredPrices);
  const { minPrice: queryMin, maxPrice: queryMax } = query;

  const from = ((queryMin ? +queryMin : filterMin) / 1_000_000)
    .toFixed(2)
    .replace(".", ",");
  const to = ((queryMax ? +queryMax : filterMax) / 1_000_000)
    .toFixed(2)
    .replace(".", ",");

  const label = `${t("prepositions.from")} ${from === "0,00" ? "0,01" : from} ${t(
    "prepositions.to",
  )} ${to === "0,0" ? "0,01" : to} ${t("prepositions.million")}`;

  const { price1, price2, price3, price4 } = predefinedPrices[currency];

  const priceLabels: Record<"1" | "2" | "3" | "4", string> = {
    "1": `${t("prepositions.lessThan")} ${price1.display.replace(
      "_symbol_",
      symbol,
    )}${currency !== "usd" ? t("prepositions.million") : ""}`,
    "2": `${price2.display.replaceAll("_symbol_", symbol)}${
      currency !== "usd" ? t("prepositions.million") : ""
    }`,
    "3": `${price3.display.replaceAll("_symbol_", symbol)}${t("prepositions.million")}`,
    "4": `${symbol}${price4.display}${t("prepositions.million")}`,
  };
  useEffect(() => {
    if (window.scrollY > 300) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selection]);

  useEffect(() => {
    if (selection && !isSelection) {
      startTransition(() => {
        deleteParam("quickSelection");
        setSelection(undefined);
      });
    }
  }, [deleteParam, isSelection, selection]);
  return (
    <>
      {!isFixed && selection && (
        <button
          key={selection.title}
          className={cl(
            styles.row__button,
            styles["row__button--active"],
            styles["row__button--mobile"],
          )}
          onClick={() => {
            startTransition(() => {
              removeMultipleParams(selection.query);
              setSelection(undefined);
            });
          }}
          disabled={
            getFilteredProperties(properties, { ...query, ...selection.query }, "usd")
              .length === 0
          }
        >
          {firstRowIcons[selection.title]}
          <P45 className={styles.filter__text}>{t(`catalog.${selection.title}`)}</P45>
          <SvgCross />
        </button>
      )}
      <div className={styles.row}>
        {(isScrollEnd || (!isScrollStart && !isScrollEnd)) && (
          <div
            className={cl(styles.row__buttonWrapper, styles["row__buttonWrapper--prev"])}
          >
            <SvgGradient isMobile={isMobile} />
            <CircleButton
              className={styles.row__sliderButton}
              as={"button"}
              content={<ArrowIcon isRight />}
              styleType="secondary"
              theme={"dark"}
              onClick={scrollPrev}
              name="scroll to previous"
            />
          </div>
        )}
        {(isScrollStart || (!isScrollStart && !isScrollEnd)) && (
          <div
            className={cl(styles.row__buttonWrapper, styles["row__buttonWrapper--next"])}
          >
            <SvgGradient isMobile={isMobile} />
            <CircleButton
              className={styles.row__sliderButton}
              as={"button"}
              content={<ArrowIcon isRight />}
              styleType="secondary"
              theme={"dark"}
              onClick={scrollNext}
              name="scroll to next"
            />
          </div>
        )}
        <div ref={emblaRef} className={styles.filter__embla} id="embla">
          <div className={cl(styles.row__first, styles.container)}>
            {isPending ? null : query.propertyType ? (
              Array.isArray(query.propertyType) ? (
                query.propertyType.map((pt) => (
                  <Chips onClick={() => removeSingleParam("propertyType", pt)} key={pt}>
                    {t(pt.toUpperCase())}
                  </Chips>
                ))
              ) : (
                <Chips onClick={() => query.propertyType && deleteParam("propertyType")}>
                  {t(query.propertyType.toUpperCase())}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.phuketDistrict || query.island === "phuket" ? (
              <Chips onClick={() => deleteParam("phuketDistrict")}>
                {t("locations.phuket")}
              </Chips>
            ) : null}

            {isPending ? null : query.samuiDistrict || query.island === "samui" ? (
              <Chips onClick={() => deleteParam("samuiDistrict")}>
                {t("locations.samui")}
              </Chips>
            ) : null}
            {isPending ? null : query.phuketDistrict ? (
              Array.isArray(query.phuketDistrict) ? (
                query.phuketDistrict.map((pd) => {
                  if (pd === "all") return null;
                  return (
                    <Chips
                      onClick={() => removeSingleParam("phuketDistrict", pd)}
                      key={pd}
                    >
                      {t("locations." + pd)}
                    </Chips>
                  );
                })
              ) : query.phuketDistrict === "all" ? null : (
                <Chips
                  onClick={() =>
                    query.phuketDistrict && setParam(undefined, { phuketDistrict: "all" })
                  }
                >
                  {t("locations." + query.phuketDistrict)}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.samuiDistrict ? (
              Array.isArray(query.samuiDistrict) ? (
                query.samuiDistrict.map((sd) => {
                  if (sd === "all") return null;
                  return (
                    <Chips
                      onClick={() => removeSingleParam("samuiDistrict", sd)}
                      key={sd}
                    >
                      {t("locations." + sd)}
                    </Chips>
                  );
                })
              ) : query.samuiDistrict === "all" ? null : (
                <Chips onClick={() => setParam(undefined, { samuiDistrict: "all" })}>
                  {t("locations." + query.samuiDistrict)}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.phuketBeaches ? (
              Array.isArray(query.phuketBeaches) ? (
                query.phuketBeaches.map((pb) => {
                  return (
                    <Chips
                      onClick={() => removeSingleParam("phuketBeaches", pb)}
                      key={pb}
                    >
                      {`${t("beach")} ${t("locations." + pb)}`}
                    </Chips>
                  );
                })
              ) : (
                <Chips onClick={() => deleteParam("phuketBeaches")}>
                  {`${t("beach")} ${t("locations." + query.phuketBeaches)}`}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.samuiBeaches ? (
              Array.isArray(query.samuiBeaches) ? (
                query.samuiBeaches.map((sb) => {
                  return (
                    <Chips onClick={() => removeSingleParam("samuiBeaches", sb)} key={sb}>
                      {`${t("beach")} ${t("locations." + sb)}`}
                    </Chips>
                  );
                })
              ) : (
                <Chips onClick={() => deleteParam("samuiBeaches")}>
                  {`${t("beach")} ${t("locations." + query.samuiBeaches)}`}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.minPrice || query.maxPrice ? (
              <Chips onClick={() => deleteParams(["minPrice", "maxPrice"])}>
                {label}
              </Chips>
            ) : null}
            {isPending ? null : query.priceButton ? (
              Array.isArray(query.priceButton) ? (
                query.priceButton.map((pb) => {
                  return (
                    <Chips onClick={() => deleteParam("priceButton")} key={pb}>
                      {priceLabels[pb]}
                    </Chips>
                  );
                })
              ) : (
                <Chips onClick={() => deleteParam("priceButton")}>
                  {priceLabels[query.priceButton]}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.bedrooms ? (
              Array.isArray(query.bedrooms) ? (
                query.bedrooms.map((b) => (
                  <Chips onClick={() => removeSingleParam("bedrooms", b)} key={b}>
                    {b === "0"
                      ? t("studio")
                      : b === "4"
                        ? "4+ " + t("declinable.bedroomsFew")
                        : b +
                          " " +
                          getWordEnding(
                            +b,
                            [
                              t("declinable.bedroom"),
                              t("declinable.bedroomsFew"),
                              t("declinable.bedroomsMore"),
                            ],
                            language,
                          )}
                  </Chips>
                ))
              ) : (
                <Chips onClick={() => query.bedrooms && deleteParam("bedrooms")}>
                  {query.bedrooms === "0"
                    ? t("studio")
                    : query.bedrooms === "4"
                      ? "4+ " + t("declinable.bedroomsFew")
                      : query.bedrooms +
                        " " +
                        getWordEnding(
                          +query.bedrooms,
                          [
                            t("declinable.bedroom"),
                            t("declinable.bedroomsFew"),
                            t("declinable.bedroomsMore"),
                          ],
                          language,
                        )}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.beachDistance ? (
              Array.isArray(query.beachDistance) ? (
                query.beachDistance.map((bd) => (
                  <Chips onClick={() => removeSingleParam("beachDistance", bd)} key={bd}>
                    {t("catalog." + beachDistance[bd])}
                  </Chips>
                ))
              ) : (
                <Chips
                  onClick={() => removeSingleParam("beachDistance", query.beachDistance)}
                >
                  {t("catalog." + beachDistance[query.beachDistance])}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.bathroomAmount ? (
              Array.isArray(query.bathroomAmount) ? (
                query.bathroomAmount.map((b) => (
                  <Chips onClick={() => removeSingleParam("bathroomAmount", b)} key={b}>
                    {b === "3"
                      ? "3+ " + t("declinable.bathroomFew")
                      : b +
                        " " +
                        getWordEnding(
                          +b,
                          [
                            t("declinable.bathroom"),
                            t("declinable.bathroomFew"),
                            t("declinable.bathroomsMore"),
                          ],
                          language,
                        )}
                  </Chips>
                ))
              ) : (
                <Chips
                  onClick={() => query.bathroomAmount && deleteParam("propertyType")}
                >
                  {query.bathroomAmount === "3"
                    ? "3+ " + t("declinable.bathroomFew")
                    : query.bathroomAmount +
                      " " +
                      getWordEnding(
                        +query.bathroomAmount,
                        [
                          t("declinable.bathroom"),
                          t("declinable.bathroomFew"),
                          t("declinable.bathroomsMore"),
                        ],
                        language,
                      )}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.investAttractiveness ? (
              Array.isArray(query.investAttractiveness) ? (
                query.investAttractiveness.map((ia) => (
                  <Chips
                    onClick={() => removeSingleParam("investAttractiveness", ia)}
                    key={ia}
                  >
                    {t(ia)}
                  </Chips>
                ))
              ) : (
                <Chips onClick={() => deleteParam("investAttractiveness")}>
                  {t(query.investAttractiveness)}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.perfectInvest ? (
              <Chips onClick={() => deleteParam("perfectInvest")}>
                {t("perfectForInvest")}
              </Chips>
            ) : null}
            {isPending ? null : query.buildingStages ? (
              Array.isArray(query.buildingStages) ? (
                query.buildingStages.map((bs) => (
                  <Chips onClick={() => removeSingleParam("buildingStages", bs)} key={bs}>
                    {t(bs)}
                  </Chips>
                ))
              ) : (
                <Chips onClick={() => deleteParam("buildingStages")}>
                  {t(query.buildingStages)}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.facilities ? (
              Array.isArray(query.facilities) ? (
                query.facilities.map((f) => (
                  <Chips onClick={() => removeSingleParam("facilities", f)} key={f}>
                    {t(f)}
                  </Chips>
                ))
              ) : (
                <Chips onClick={() => deleteParam("facilities")}>
                  {t(query.facilities)}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.view ? (
              Array.isArray(query.view) ? (
                query.view.map((v) => (
                  <Chips onClick={() => removeSingleParam("view", v)} key={v}>
                    {t("view")} {t("ON_" + v)}
                  </Chips>
                ))
              ) : (
                <Chips onClick={() => deleteParam("view")}>
                  {t("view")} {t("ON_" + query.view)}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.landscape ? (
              Array.isArray(query.landscape) ? (
                query.landscape.map((l) => (
                  <Chips onClick={() => removeSingleParam("landscape", l)} key={l}>
                    {t(l)[0].toUpperCase() + t(l).slice(1)}
                  </Chips>
                ))
              ) : (
                <Chips onClick={() => deleteParam("landscape")}>
                  {t(query.landscape)[0].toUpperCase() + t(query.landscape).slice(1)}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.ownershipType ? (
              Array.isArray(query.ownershipType) ? (
                query.ownershipType.map((ot) => (
                  <Chips onClick={() => removeSingleParam("ownershipType", ot)} key={ot}>
                    {t(ot)[0].toUpperCase() + t(ot).slice(1)}
                  </Chips>
                ))
              ) : (
                <Chips onClick={() => deleteParam("ownershipType")}>
                  {t(query.ownershipType)[0].toUpperCase() +
                    t(query.ownershipType).slice(1)}
                </Chips>
              )
            ) : null}
            {isPending ? null : query.videoReview ? (
              <Chips onClick={() => deleteParam("videoReview")}>{t("videoReview")}</Chips>
            ) : null}
            {firstRowItems.map((item) => {
              const isActive =
                selection?.query.quickSelection === item.query.quickSelection;
              const icon = firstRowIcons[item.title];
              const isDesktopHidden = (selection && !isActive) || (isActive && isPending);
              if (
                (isMobile && !!selection && !isFixed) ||
                (isFixed && !isActive && selection)
              )
                return null;

              return (
                <button
                  key={item.title}
                  className={cl(
                    styles.row__button,
                    isActive && styles["row__button--active"],
                    isDesktopHidden && styles["row__button--desktop-hidden"],
                  )}
                  onClick={() => {
                    if (isActive) {
                      startTransition(() => {
                        removeMultipleParams(item.query);
                        setSelection(undefined);
                        emblaApi?.scrollTo(0);
                      });
                    } else {
                      startTransition(() => {
                        /** доп.запись параметров */
                        // const q = { ...item.query };
                        // if (Object.keys(paginatedQuery).length > 0) {
                        //   delete q.quickSelection;
                        // }
                        // changeSelection(selection?.query, q);

                        /** перезапись параметров */
                        setParam(undefined, { ...item.query }, true);

                        setSelection(item);
                        emblaApi?.scrollTo(0);
                      });
                    }
                  }}
                  disabled={
                    getFilteredProperties(properties, { ...query, ...item.query }, "usd")
                      .length === 0
                  }
                >
                  {icon}
                  <P45 className={styles.filter__text}>{t(`catalog.${item.title}`)}</P45>
                  {isActive && <SvgCross />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
